// Central mock data store - generates and caches all data
import { 
  SalesRep, Product, Account, Contact, Lead, Opportunity, Activity, Funnel, FunnelStage 
} from './types';
import { 
  generateSalesReps, generateProducts, generateAccounts, generateContacts,
  generateLeads, generateOpportunities, generateActivities 
} from './generators';

// Singleton data store
class MockDataStore {
  private static instance: MockDataStore;
  
  public salesReps: SalesRep[] = [];
  public products: Product[] = [];
  public accounts: Account[] = [];
  public contacts: Contact[] = [];
  public leads: Lead[] = [];
  public opportunities: Opportunity[] = [];
  public activities: Activity[] = [];
  public funnels: Funnel[] = [];
  
  private initialized = false;
  private _isCRM = false;
  
  private constructor() {}
  
  static getInstance(): MockDataStore {
    if (!MockDataStore.instance) {
      MockDataStore.instance = new MockDataStore();
    }
    return MockDataStore.instance;
  }
  
  get isCRM(): boolean {
    return this._isCRM;
  }
  
  initialize(): void {
    if (this.initialized) return;
    
    console.log('[MockDataStore] Generating simulated data...');
    this._generateMockData();
    this.initialized = true;
  }

  private _generateMockData(): void {
    // Generate base data
    this.salesReps = generateSalesReps(10);
    this.products = generateProducts();
    this.accounts = generateAccounts(300);
    this.contacts = generateContacts(this.accounts);
    
    // Generate relational data
    this.leads = generateLeads(this.accounts, this.contacts, this.salesReps, 1000);
    this.opportunities = generateOpportunities(
      this.accounts, this.contacts, this.salesReps, this.products, this.leads, 200
    );
    this.activities = generateActivities(this.leads, this.opportunities, this.salesReps, 2000);
    
    // Hydrate references
    this.leads = this.leads.map(lead => ({
      ...lead,
      account: this.accounts.find(a => a.id === lead.accountId),
      contact: this.contacts.find(c => c.id === lead.contactId),
      owner: this.salesReps.find(r => r.id === lead.ownerId),
    }));
    
    this.opportunities = this.opportunities.map(opp => ({
      ...opp,
      account: this.accounts.find(a => a.id === opp.accountId),
      contact: this.contacts.find(c => c.id === opp.contactId),
      owner: this.salesReps.find(r => r.id === opp.ownerId),
      product: this.products.find(p => p.id === opp.productId),
    }));
    
    // Create default funnel
    this.funnels = [this.createDefaultFunnel()];
    
    this._isCRM = false;
    console.log('[MockDataStore] Simulated data ready:', {
      reps: this.salesReps.length,
      accounts: this.accounts.length,
      leads: this.leads.length,
      opportunities: this.opportunities.length,
      activities: this.activities.length,
    });
  }

  /** Inject real CRM data, replacing all mock data */
  injectCRMData(data: {
    salesReps: SalesRep[];
    accounts: Account[];
    contacts: Contact[];
    leads: Lead[];
    opportunities: Opportunity[];
    activities: Activity[];
    funnels: Funnel[];
  }): void {
    this.salesReps = data.salesReps;
    this.accounts = data.accounts;
    this.contacts = data.contacts;
    this.leads = data.leads;
    this.opportunities = data.opportunities;
    this.activities = data.activities;
    this.funnels = data.funnels;
    this.products = [{ id: 'crm-product', name: 'Default', tier: 'Growth', monthlyPrice: 0, annualPrice: 0, description: '' }];
    this._isCRM = true;
    this.initialized = true;
    console.log('[MockDataStore] CRM data injected:', {
      reps: this.salesReps.length,
      accounts: this.accounts.length,
      leads: this.leads.length,
      opportunities: this.opportunities.length,
      activities: this.activities.length,
    });
  }

  /** Revert to simulated data */
  revertToMock(): void {
    console.log('[MockDataStore] Reverting to simulated data...');
    this.initialized = false;
    this._generateMockData();
    this.initialized = true;
  }
  
  private createDefaultFunnel(): Funnel {
    const stages: FunnelStage[] = [
      { id: 'stage-1', name: 'New Lead', order: 1, category: 'lead', color: 'hsl(var(--chart-1))' },
      { id: 'stage-2', name: 'Contacted', order: 2, category: 'lead', color: 'hsl(var(--chart-2))' },
      { id: 'stage-3', name: 'MQL', order: 3, category: 'lead', color: 'hsl(var(--chart-3))' },
      { id: 'stage-4', name: 'SQL', order: 4, category: 'qualified', color: 'hsl(var(--chart-4))' },
      { id: 'stage-5', name: 'Discovery', order: 5, category: 'pipeline', color: 'hsl(var(--chart-5))' },
      { id: 'stage-6', name: 'Proposal', order: 6, category: 'pipeline', color: 'hsl(var(--chart-1))' },
      { id: 'stage-7', name: 'Negotiation', order: 7, category: 'pipeline', color: 'hsl(var(--chart-2))' },
      { id: 'stage-8', name: 'Closed Won', order: 8, category: 'won', color: 'hsl(var(--success))' },
      { id: 'stage-9', name: 'Closed Lost', order: 9, category: 'lost', color: 'hsl(var(--destructive))' },
    ];
    
    return {
      id: 'funnel-default',
      name: 'Default SaaS Funnel',
      description: 'Standard B2B SaaS sales funnel',
      stages,
      isDefault: true,
      createdAt: new Date(),
    };
  }
  
  // Helper methods for data access
  getLeadsByOwner(ownerId: string): Lead[] {
    return this.leads.filter(l => l.ownerId === ownerId);
  }
  
  getOpportunitiesByOwner(ownerId: string): Opportunity[] {
    return this.opportunities.filter(o => o.ownerId === ownerId);
  }
  
  getOpportunitiesByStage(stage: string): Opportunity[] {
    return this.opportunities.filter(o => o.stage === stage);
  }
  
  getActivitiesByLead(leadId: string): Activity[] {
    return this.activities.filter(a => a.leadId === leadId);
  }
  
  getActivitiesByOpportunity(oppId: string): Activity[] {
    return this.activities.filter(a => a.opportunityId === oppId);
  }
  
  getRepById(repId: string): SalesRep | undefined {
    return this.salesReps.find(r => r.id === repId);
  }
}

// Export singleton instance
export const mockData = MockDataStore.getInstance();

// Initialize on import
mockData.initialize();
